'use client';

import ActionButton from '@/components/form/ActionButton/ActionButton';
import FormText from '@/components/form/FormText/FormText';
import ContentPageTitle from '@/components/ui/ContentPageTitle/ContentPageTitle';
import { swal } from '@/lib/sweetalert';
import { yup } from '@/lib/yup';
import layoutStyles from '@/styles/modules/layout.module.scss';
import utilsStyles from '@/styles/modules/utils.module.scss';
import { pickErrorMessages } from '@/utils/functions';
import { yupResolver } from '@hookform/resolvers/yup';
import clsx from 'clsx';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { postOrderInquiry } from '../../api/postOrderInquiry';
import type { OrderInquiryResult } from '../../types';
import GuestOrderInquiryResult from './GuestOrderInquiryResult';

const formScheme = yup.object({
  email: yup.string().label('連絡先メールアドレス').email().required(),
  orderId: yup.string().label('注文ID').required(),
});

function GuestOrderInquiry() {
  const [isLoading, setIsLoading] = useState(false);
  const [inquiryResult, setInquiryResult] = useState<OrderInquiryResult | null>(null);

  const { handleSubmit, control, reset } = useForm({
    mode: 'onChange',
    defaultValues: { email: '', orderId: '' },
    resolver: yupResolver(formScheme),
  });

  const submit = async (data: { email: string; orderId: string }) => {
    try {
      setIsLoading(true);
      const { result } = await postOrderInquiry({ orderId: data.orderId, email: data.email });
      setInquiryResult(result);
      requestAnimationFrame(() => {
        window.scrollTo(0, 0);
      });
    } catch (error) {
      await swal.messages({ messages: pickErrorMessages(error) });
    } finally {
      setIsLoading(false);
    }
  };

  const submitError = async () => {
    await swal.invalid();
  };

  const onBack = () => {
    reset({ orderId: '' });
    setInquiryResult(null);
    // iOS Safariではstate更新後のレンダリング完了前にscrollToが効かないため、次フレームで実行
    requestAnimationFrame(() => {
      window.scrollTo(0, 0);
    });
  };

  return (
    <div
      className={clsx(
        inquiryResult ? layoutStyles.gfPageContainerLargeNoPaddingSp : layoutStyles.gfPageContainerXSmall,
        utilsStyles.mtGfContentTitle,
      )}
    >
      <ContentPageTitle title='注文履歴' pcTextAlign='Center' spTextAlign='Center' />
      {inquiryResult ? (
        <GuestOrderInquiryResult result={inquiryResult} onBack={onBack} />
      ) : (
        <>
          <p className={`${utilsStyles.textCenter} ${utilsStyles.mtXSmall}`}>
            ご購入時の連絡先メールアドレスと注文IDを入力してください
          </p>
          <form onSubmit={handleSubmit(submit, submitError)}>
            <div className={`${layoutStyles.formWrapper} ${utilsStyles.mtSmall}`}>
              <FormText type='email' label='連絡先メールアドレス' name='email' control={control} autocomplete='email' />
              <FormText label='注文ID' name='orderId' control={control} />
            </div>
            <div className={`${layoutStyles.centeredBtnWrapper} ${utilsStyles.mtSmall}`}>
              <ActionButton type='submit' bgColor='primary' color='white' isLoading={isLoading}>
                購入履歴を表示する
              </ActionButton>
            </div>
          </form>
        </>
      )}
    </div>
  );
}

export default GuestOrderInquiry;
